import React, { useMemo, useRef, useState } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import Papa from "papaparse";
import {
  apiService,
  ReferenceStatusResult,
  REFERENCE_STATUS_BATCH_SIZE,
} from "../../../api/api";
import { trackToolAction } from "../../../utils/analytics";
import { withExportDate } from "../../../utils/exportFileName";
import { parseReferenceReport, ReportDetails } from "../Components/Utils/referenceReport";
import { KNOWN_CMS_DISPLAY_NAMES } from "../../BannerSwap/BannerSwapComponents/cmsDisplayUtils";

const CMS_OPTIONS = Object.entries(KNOWN_CMS_DISPLAY_NAMES)
  .map(([value, label]) => ({ label, value }))
  .sort((a, b) => a.label.localeCompare(b.label));

const parseReferenceIds = (input: string): string[] =>
  Array.from(
    new Set(
      input
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    )
  );

type ReferenceStatusRow = ReferenceStatusResult & ReportDetails;

const EMPTY_DETAILS: ReportDetails = { claimedVideos: null, assetTitle: "", assetCustomId: "" };

/** Highest daily engaged views first, then most claimed videos; rows without data go last. */
const byReactivationPriority = (a: ReferenceStatusRow, b: ReferenceStatusRow) =>
  (b.dailyEngagedViews ?? -1) - (a.dailyEngagedViews ?? -1) ||
  (b.claimedVideos ?? -1) - (a.claimedVideos ?? -1);

const getErrorMessage = (err: unknown): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { message?: string; error?: string } } })
      .response?.data;
    if (data?.message || data?.error) return (data.message || data.error) as string;
  }
  return err instanceof Error ? err.message : "Lookup failed";
};

const statusSeverity = (
  status: string
): "success" | "warning" | "danger" | "info" => {
  if (status === "active") return "success";
  if (status === "inactive") return "warning";
  if (status === "deleted") return "danger";
  return "info";
};

const ReferenceStatusTab: React.FC = () => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stopRequested = useRef(false);

  const [referenceIdInput, setReferenceIdInput] = useState("");
  const [cmsId, setCmsId] = useState("7A8nsWSEMsFcVZBw1q94rg");
  const [results, setResults] = useState<ReferenceStatusResult[]>([]);
  const [reportDetails, setReportDetails] = useState<Map<string, ReportDetails>>(new Map());
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const parsedIds = useMemo(
    () => parseReferenceIds(referenceIdInput),
    [referenceIdInput]
  );

  const rows = useMemo<ReferenceStatusRow[]>(
    () =>
      results
        .map((r) => {
          const report = reportDetails.get(r.referenceId) ?? EMPTY_DETAILS;
          return {
            ...r,
            claimedVideos: r.claimedVideos ?? report.claimedVideos,
            assetTitle: r.assetTitle || report.assetTitle,
            assetCustomId: report.assetCustomId,
          };
        })
        .sort(byReactivationPriority),
    [results, reportDetails]
  );

  const viewsDataDate = useMemo(
    () => results.find((r) => r.viewsDataDate)?.viewsDataDate ?? "",
    [results]
  );

  const summary = useMemo(() => {
    const ok = results.filter((r) => !r.error);
    const countBy = (key: "status" | "statusReason") =>
      Object.entries(
        ok.reduce<Record<string, number>>((acc, r) => {
          const value = r[key] || (key === "statusReason" ? "(none)" : "(unknown)");
          acc[value] = (acc[value] ?? 0) + 1;
          return acc;
        }, {})
      ).sort((a, b) => b[1] - a[1]);
    return {
      total: results.length,
      active: ok.filter((r) => r.status === "active").length,
      inactiveOrDeleted: ok.filter((r) => ["inactive", "deleted"].includes(r.status)).length,
      errors: results.length - ok.length,
      byStatus: countBy("status"),
      byStatusReason: countBy("statusReason"),
    };
  }, [results]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (parsed) => {
        const { ids, details } = parseReferenceReport(parsed.data);
        setReferenceIdInput(ids.join("\n"));
        setReportDetails(details);
        toast.current?.show({
          severity: "info",
          summary: "CSV loaded",
          detail: `Loaded ${ids.length} reference ID(s) from ${file.name}.`,
        });
      },
    });
  };

  /** Rows pasted from a spreadsheet (tab-separated, with a header) keep their report columns. */
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t")) return;
    const { ids, details, hasClaimsColumn } = parseReferenceReport(
      text.split(/\r?\n/).filter((line) => line.trim()).map((line) => line.split("\t"))
    );
    if (!hasClaimsColumn) return;
    e.preventDefault();
    setReferenceIdInput(ids.join("\n"));
    setReportDetails(details);
    toast.current?.show({
      severity: "info",
      summary: "Table pasted",
      detail: `Loaded ${ids.length} reference ID(s) with claimed videos.`,
    });
  };

  const handleLookup = async () => {
    if (parsedIds.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No reference IDs",
        detail: "Please enter at least one reference ID.",
      });
      return;
    }

    trackToolAction("YouTubeReferenceStatus", "lookup", { count: parsedIds.length });
    stopRequested.current = false;
    setLoading(true);
    setResults([]);
    setProgress({ done: 0, total: parsedIds.length });

    const collected: ReferenceStatusResult[] = [];
    let viewsWarningShown = false;
    try {
      for (let i = 0; i < parsedIds.length; i += REFERENCE_STATUS_BATCH_SIZE) {
        if (stopRequested.current) break;
        const batch = parsedIds.slice(i, i + REFERENCE_STATUS_BATCH_SIZE);
        const { results: batchResults, viewsWarning } =
          await apiService.youtubeReferenceStatus.lookupReferenceStatus(batch, cmsId);
        if (viewsWarning && !viewsWarningShown) {
          viewsWarningShown = true;
          toast.current?.show({ severity: "warn", summary: "Views unavailable", detail: viewsWarning, life: 8000 });
        }
        collected.push(...batchResults);
        setResults([...collected]);
        setProgress({ done: collected.length, total: parsedIds.length });
      }
      toast.current?.show({
        severity: stopRequested.current ? "warn" : "success",
        summary: stopRequested.current ? "Lookup stopped" : "Lookup complete",
        detail: `Checked ${collected.length} of ${parsedIds.length} reference(s).`,
        life: 5000,
      });
    } catch (err: unknown) {
      toast.current?.show({
        severity: "error",
        summary: "Lookup failed",
        detail: `${getErrorMessage(err)} (${collected.length} of ${parsedIds.length} checked before the failure — results so far are kept).`,
        life: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    trackToolAction("YouTubeReferenceStatus", "export");
    const headers: (keyof ReferenceStatusRow)[] = [
      "referenceId",
      "cmsId",
      "httpStatus",
      "status",
      "statusReason",
      "claimedVideos",
      "dailyEngagedViews",
      "viewsDataDate",
      "assetTitle",
      "assetCustomId",
      "duplicateLeader",
      "assetId",
      "contentType",
      "length",
      "videoId",
      "claimId",
      "originationSource",
      "excludedIntervalsCount",
      "error",
    ];
    const lines = rows.map((r) =>
      headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${withExportDate("Reference Status Audit")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const statusBodyTemplate = (row: ReferenceStatusResult) => {
    if (row.error) return <Tag value="error" severity="danger" />;
    return <Tag value={row.status || "—"} severity={statusSeverity(row.status)} />;
  };

  const statusReasonBodyTemplate = (row: ReferenceStatusResult) =>
    row.statusReason ? (
      <span style={{ fontFamily: "monospace" }}>{row.statusReason}</span>
    ) : (
      <span style={{ color: "#888" }}>—</span>
    );

  const numberBodyTemplate =
    (field: "claimedVideos" | "dailyEngagedViews") => (row: ReferenceStatusRow) =>
      row[field] === null ? (
        <span style={{ color: "#888" }}>—</span>
      ) : (
        row[field]!.toLocaleString()
      );

  const errorBodyTemplate = (row: ReferenceStatusResult) =>
    row.error ? <span className="text-red-500 text-sm">{row.error}</span> : null;

  const breakdownTable = (title: string, rows: [string, number][]) => (
    <Card title={title} style={{ flex: "1 1 300px" }}>
      <DataTable
        value={rows.map(([value, count]) => ({ value, count }))}
        className="p-datatable-sm"
        emptyMessage="—"
      >
        <Column field="value" header="Value" style={{ fontFamily: "monospace" }} />
        <Column field="count" header="Count" style={{ width: "90px" }} />
      </DataTable>
    </Card>
  );

  return (
    <div style={{ padding: "1rem 0" }}>
      <Toast ref={toast} />

      <Card title="Reference Status Lookup" style={{ marginBottom: "1.5rem" }}>
        <div className="flex flex-column gap-3">
          <p className="m-0 text-sm text-color-secondary">
            Calls the Content ID <code>references.get</code> method for each
            reference and returns its <code>status</code> and{" "}
            <code>statusReason</code>. YouTube only returns a statusReason for
            inactive or deleted references.
          </p>

          <div className="flex flex-column gap-2" style={{ maxWidth: "320px" }}>
            <label className="font-medium text-sm">Content Owner (CMS)</label>
            <Dropdown
              value={cmsId}
              options={CMS_OPTIONS}
              onChange={(e) => setCmsId(e.value)}
              disabled={loading}
            />
          </div>

          <div className="flex flex-column gap-2">
            <label className="font-medium text-sm">
              Reference IDs{" "}
              <span className="font-normal text-color-secondary">
                (one per line or comma-separated, or upload a CSV)
              </span>
            </label>
            <InputTextarea
              value={referenceIdInput}
              onChange={(e) => setReferenceIdInput(e.target.value)}
              onPaste={handlePaste}
              rows={8}
              placeholder={"Paste reference IDs here..."}
              style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
              disabled={loading}
            />
            <small className="text-color-secondary">
              {parsedIds.length} unique reference ID(s)
            </small>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              label={loading ? "Checking…" : "Fetch statusReason"}
              icon="pi pi-search"
              loading={loading}
              disabled={loading || parsedIds.length === 0}
              onClick={handleLookup}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <Button
              label="Upload CSV"
              icon="pi pi-upload"
              severity="secondary"
              outlined
              disabled={loading}
              onClick={() => fileInputRef.current?.click()}
            />
            {loading && (
              <Button
                label="Stop"
                icon="pi pi-stop"
                severity="danger"
                outlined
                onClick={() => {
                  stopRequested.current = true;
                }}
              />
            )}
            {results.length > 0 && (
              <Button
                label="Export CSV"
                icon="pi pi-download"
                severity="secondary"
                onClick={exportCsv}
                disabled={loading}
              />
            )}
          </div>

          {loading && progress.total > 0 && (
            <div className="flex flex-column gap-1">
              <ProgressBar
                value={Math.round((progress.done / progress.total) * 100)}
                style={{ height: "8px" }}
                showValue={false}
              />
              <small className="text-color-secondary">
                Checked {progress.done} / {progress.total}
              </small>
            </div>
          )}
        </div>
      </Card>

      {results.length > 0 && (
        <>
          <div className="flex gap-3 flex-wrap" style={{ marginBottom: "1rem" }}>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-primary">{summary.total}</div>
              <div className="text-sm text-color-secondary mt-1">References checked</div>
            </Card>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-green-500">{summary.active}</div>
              <div className="text-sm text-color-secondary mt-1">Active</div>
            </Card>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-orange-500">
                {summary.inactiveOrDeleted}
              </div>
              <div className="text-sm text-color-secondary mt-1">Inactive / deleted</div>
            </Card>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-red-500">{summary.errors}</div>
              <div className="text-sm text-color-secondary mt-1">Errors</div>
            </Card>
          </div>

          <div className="flex gap-3 flex-wrap" style={{ marginBottom: "1rem" }}>
            {breakdownTable("By status", summary.byStatus)}
            {breakdownTable("By statusReason", summary.byStatusReason)}
          </div>

          <Divider />

          {viewsDataDate && (
            <small className="text-color-secondary">
              Sorted by daily engaged views, then claimed videos, both from the CMS reports of{" "}
              {viewsDataDate}. Claimed videos are per reference; daily engaged views are per asset,
              so references on the same asset share the same figure.
            </small>
          )}

          <DataTable
            value={rows}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            filterDisplay="row"
            emptyMessage="No results"
            className="p-datatable-sm"
            style={{ marginTop: "0.5rem" }}
          >
            <Column
              field="referenceId"
              header="Reference ID"
              filter
              filterPlaceholder="Search"
              sortable
              style={{ fontFamily: "monospace", minWidth: "140px" }}
            />
            <Column
              field="status"
              header="Status"
              body={statusBodyTemplate}
              filter
              filterPlaceholder="Search"
              sortable
              style={{ minWidth: "110px" }}
            />
            <Column
              field="statusReason"
              header="Status Reason"
              body={statusReasonBodyTemplate}
              filter
              filterPlaceholder="Search"
              sortable
              style={{ minWidth: "200px" }}
            />
            <Column
              field="claimedVideos"
              header="Claimed Videos"
              body={numberBodyTemplate("claimedVideos")}
              sortable
              style={{ minWidth: "120px" }}
            />
            <Column
              field="dailyEngagedViews"
              header="Daily Engaged Views"
              body={numberBodyTemplate("dailyEngagedViews")}
              sortable
              style={{ minWidth: "140px" }}
            />
            <Column
              field="assetTitle"
              header="Asset Title"
              filter
              filterPlaceholder="Search"
              sortable
              style={{ minWidth: "180px" }}
            />
            <Column
              field="duplicateLeader"
              header="Duplicate Leader"
              sortable
              style={{ fontFamily: "monospace", minWidth: "140px" }}
            />
            <Column
              field="assetId"
              header="Asset ID"
              filter
              filterPlaceholder="Search"
              sortable
              style={{ fontFamily: "monospace", minWidth: "140px" }}
            />
            <Column field="contentType" header="Content Type" sortable />
            <Column field="length" header="Length (s)" sortable />
            <Column
              field="videoId"
              header="Video ID"
              sortable
              style={{ fontFamily: "monospace" }}
            />
            <Column field="error" header="Error" body={errorBodyTemplate} />
          </DataTable>
        </>
      )}
    </div>
  );
};

export default ReferenceStatusTab;
